import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';

import { NarrowSnappingBottomSheet } from '@mopla/ride-hailing/ui-shared';
import {
	IMplSnappingBottomSheetProps,
	InfoIcon,
	MplInputV2,
	SnapPoint,
	StationIcon,
} from '@mopla/ui';

import { useStopSearch } from '../hooks';
import { IStop } from '../types';

interface IStopSearchBottomSheetProps {
	open: boolean;
	operatorId: string | undefined;
	onClose: () => void;
	onSelect: (stop: IStop) => void;
	/** Currently selected stop — prefills the search field when reopening to edit. */
	selectedStop?: IStop;
}

const snapPoints = [SnapPoint.vh(0), SnapPoint.vh(0.9)];

const animationConfig: IMplSnappingBottomSheetProps['animationConfig'] = {
	frequency: 0.25,
	damping: 0.82,
};

/**
 * Step 2b — entry-stop (Einstiegshaltestelle) search modal.
 *
 * Swipe-down sheet with a text input and operator stop suggestions (§7c).
 * Selecting a stop fills the field and closes the sheet. Only operator stops
 * are shown — no myway / journey-search data.
 */
export const StopSearchBottomSheet: FC<IStopSearchBottomSheetProps> = ({
	open,
	operatorId,
	onClose,
	onSelect,
	selectedStop,
}) => {
	const { t } = useTranslation('ticketshop');
	const [snapIndex, setSnapIndex] = useState(0);
	const [query, setQuery] = useState('');

	const { stops, isSearchPending } = useStopSearch(operatorId, query);

	// Sync snap with open state: open → snap 1, close → snap 0.
	useEffect(() => {
		setSnapIndex(open ? 1 : 0);
	}, [open]);

	// Prefill the query with the selected stop each time the sheet opens (empty
	// when nothing is selected yet), so reopening to edit shows the current stop.
	useEffect(() => {
		if (open) {
			setQuery(selectedStop?.name ?? '');
		}
	}, [open, selectedStop]);

	const handleSnapIndexChange = useCallback(
		(index: number) => {
			setSnapIndex(index);
			if (index === 0) {
				onClose();
			}
		},
		[onClose]
	);

	const handleSelect = useCallback(
		(stop: IStop) => {
			onSelect(stop);
			onClose();
		},
		[onSelect, onClose]
	);

	// `stops` is already empty while a newer query is pending, so suggestions of
	// the previous query never flash before the "no results" message appears.
	const showSuggestions = query.trim().length > 0 && stops.length > 0;
	const showNoResults =
		query.trim().length > 0 && !showSuggestions && !isSearchPending;

	return (
		<NarrowSnappingBottomSheet
			open={open}
			snapPoints={snapPoints}
			currentSnapIndex={snapIndex}
			onSnapIndexChange={handleSnapIndexChange}
			animationConfig={animationConfig}
			unmountOnClose
			showBackdrop
			onBackdropClick={onClose}
			header={
				<Stack gap={2} sx={{ pt: 1.5, pb: 2 }}>
					<Typography variant="h4-sb" color="primary.main">
						{t('ticket_detail.stop_search.title')}
					</Typography>
					<MplInputV2
						variant="outlined"
						label={t('ticket_detail.stop_search.input_label')}
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						clearable
						autoFocus
						fullWidth
						onFocus={(e) => e.target.select()}
						inputProps={{ 'data-testid': 'stop-search-input' }}
					/>
					<Stack
						direction="row"
						alignItems="center"
						gap={1}
						sx={{
							bgcolor: 'solutions.50',
							borderRadius: 2,
							px: 1.5,
							py: 1.5,
						}}
					>
						<Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>
							<InfoIcon />
						</Box>
						<Typography variant="b2" color="primary.main" sx={{ flex: 1 }}>
							{t('ticket_detail.stop_search.required_hint')}
						</Typography>
					</Stack>
				</Stack>
			}
		>
			{/* mt:-2 cancels DrawerContentArea's forced 16px paddingTop, so the
			    gap below the sticky warning stays a constant 16px (header pb). */}
			<Stack gap={2} sx={{ pb: 4, mt: -2 }}>
				{showSuggestions && (
					<Stack gap={0.5} data-testid="stop-search-suggestions">
						<Typography variant="l1" color="solutions.600">
							{t('ticket_detail.stop_search.suggestions_label')}
						</Typography>
						{stops.map((stop) => (
							<ButtonBase
								key={stop.id}
								onClick={() => handleSelect(stop)}
								data-testid="stop-search-suggestion"
								sx={{
									width: '100%',
									justifyContent: 'flex-start',
									textAlign: 'left',
									py: '9px',
								}}
							>
								<Stack
									direction="row"
									alignItems="center"
									gap={1}
									sx={{ width: '100%' }}
								>
									<StationIcon />

									<Stack flex={1} minWidth={0}>
										<Typography variant="b2" color="primary.main" noWrap>
											{stop.name}
										</Typography>
										{stop.address && (
											<Typography variant="l1" color="solutions.600" noWrap>
												{stop.address}
											</Typography>
										)}
									</Stack>
								</Stack>
							</ButtonBase>
						))}
					</Stack>
				)}

				{showNoResults && (
					<Typography
						variant="b2"
						color="solutions.600"
						data-testid="stop-search-empty"
					>
						{t('ticket_detail.stop_search.no_results')}
					</Typography>
				)}
			</Stack>
		</NarrowSnappingBottomSheet>
	);
};
